/**
 * Feuille de brouillon — la palette : bouts ET opérations dans UN SEUL flux
 * en ligne (décision du 11/10/2026, remplace les opérations en grille).
 *
 * Ordre : bouts de l'énoncé, résultats de l'élève, réponses proposées, valeurs,
 * puis le groupe replié « outils de la carte » (marteau : grandeurs de la
 * SKILLCARD, V₀, V₁, k…), les opérations, « ⌫ » et enfin « J'ai fini » qui
 * ferme la feuille (sous le pouce, contrairement à la croix de l'en-tête).
 * Cibles de 44 px ; trop de bouts → ils rapetissent (jusqu'à 29 px) pour que
 * tout tienne sans défiler (`palette-fit.ts`).
 */

import { Fragment, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { nextPaletteSize, type PaletteSize } from './palette-fit';
import { chipKey, signTableOffered, type PaletteChip } from '@jeraisonne/reasoner';
import { disp, lineText, needsMath, spokenAtom, type AtomItem } from './line-items';
import { AtomsMath, MathListsContext } from './math-view';
import { programOf, type ScratchSession, type SessionState } from './scratch-session';
import { FREE_DIGITS } from './free-sheet';
import { Check, Delete, Hammer, Table2 } from 'lucide-react';
import { SignTableHost, openSignTableEditor } from './sign-table';
import { GridTableHost, openGridTableEditor } from './grid-table';
import { vibrate } from './use-long-press';

function chipContent(chip: { atoms: string[]; text: string }): ReactNode {
  return needsMath(chip.atoms) ? <AtomsMath atoms={chip.atoms} /> : disp(chip.text);
}

function CopyIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="6.5" y="3" width="11" height="7" rx="2" />
      <path d="M12 13v8M8.5 17.5L12 21l3.5-3.5" />
    </svg>
  );
}

const OP_LABEL: Record<string, string> = {
  '÷': 'Ajouter une barre de fraction',
  '²': 'Ajouter « au carré »',
  '√': 'Ajouter une racine carrée',
  '(': 'Ajouter une parenthèse ouvrante',
  ')': 'Ajouter une parenthèse fermante',
  ou: 'Ajouter « ou »',
  '^': 'Ajouter une puissance',
  ln: 'Ajouter le logarithme népérien',
  log: 'Ajouter le logarithme décimal',
  exp: 'Ajouter l’exponentielle',
  cos: 'Ajouter cosinus',
  sin: 'Ajouter sinus',
  tan: 'Ajouter tangente',
  arccos: 'Ajouter arccos (angle dont le cosinus vaut…)',
  arcsin: 'Ajouter arcsin (angle dont le sinus vaut…)',
  arctan: 'Ajouter arctan (angle dont la tangente vaut…)',
  π: 'Ajouter π',
  '≡': 'Ajouter « est congru à »',
  '→': 'Ajouter « tend vers »',
  '∼': 'Ajouter « est équivalent à »',
  '+∞': 'Ajouter plus l’infini',
  '−∞': 'Ajouter moins l’infini',
  '!': 'Ajouter « factorielle »',
  parmi: 'Ajouter « parmi » (k parmi n : nombre de façons de choisir k éléments parmi n)',
  '>': 'Ajouter « est supérieur à »',
  '<': 'Ajouter « est inférieur à »',
  '≥': 'Ajouter « est supérieur ou égal à »',
  '≤': 'Ajouter « est inférieur ou égal à »',
  '≈': 'Ajouter « est environ égal à » (valeur arrondie)',
  '|': 'Ajouter une barre de module (|z|)',
  arg: 'Ajouter l’argument',
  // raisonnement géométrique (moteur v4)
  '//': 'Ajouter « est parallèle à »',
  '⊥': 'Ajouter « est perpendiculaire à »',
  'milieu de': 'Ajouter « milieu de »',
  '∈': 'Ajouter « appartient à »',
  parallélogramme: 'Ajouter « parallélogramme »',
  'rectangle en': 'Ajouter « rectangle en »',
  // géométrie déductive du noyau (moteur v25)
  '∦': 'Ajouter « n’est pas parallèle à »',
  rectangle: 'Ajouter « rectangle »',
  losange: 'Ajouter « losange »',
  carré: 'Ajouter « carré »',
  'isocèle en': 'Ajouter « isocèle en »',
  'n’est pas rectangle': 'Ajouter « n’est pas rectangle »',
  'médiatrice de': 'Ajouter « médiatrice de »',
  'cercle de diamètre': 'Ajouter « cercle de diamètre »',
  'symétrique de': 'Ajouter « symétrique de »',
  'par rapport à': 'Ajouter « par rapport à »',
  '≠': 'Ajouter « est différent de »',
  // trace Python (moteur v28)
  ';': 'Ajouter « ; » (une autre variable sur la même ligne)',
  '%': 'Ajouter « % » (reste de la division)',
};

/** calcul matriciel (moteur v30) : les signes propres aux matrices */
const MATRIX_OP_LABEL: Record<string, string> = {
  '[': 'Ouvrir une matrice (crochet ouvrant)',
  ']': 'Fermer la matrice (crochet fermant)',
  ',': 'Coefficient suivant, sur la même ligne de la matrice',
  ';': 'Ligne suivante de la matrice',
  '⁻¹': 'Ajouter « inverse » (A⁻¹)',
  ᵀ: 'Ajouter « transposée » (Aᵀ)',
};

/** signes d'une ligne de CODE Python (moteur v29, compléter une ligne) */
const CODE_OP_LABEL: Record<string, string> = {
  '(': 'Ajouter une parenthèse ouvrante',
  ')': 'Ajouter une parenthèse fermante',
  ':': 'Ajouter « : » (fin d’une ligne for, while, if, def)',
  ',': 'Ajouter une virgule',
  '[': 'Ajouter un crochet ouvrant',
  ']': 'Ajouter un crochet fermant',
  '.': 'Ajouter un point',
  '+': 'Ajouter plus',
  '-': 'Ajouter moins',
  '*': 'Ajouter « * » (multiplier)',
  '/': 'Ajouter « / » (diviser)',
  '//': 'Ajouter « // » (quotient entier)',
  '%': 'Ajouter « % » (reste de la division)',
  '**': 'Ajouter « ** » (puissance)',
  '<': 'Ajouter « < »',
  '>': 'Ajouter « > »',
  '<=': 'Ajouter « <= » (inférieur ou égal)',
  '>=': 'Ajouter « >= » (supérieur ou égal)',
  '==': 'Ajouter « == » (est égal à ?)',
  '!=': 'Ajouter « != » (est différent de ?)',
  '=': 'Ajouter « = » (affecter une valeur)',
  '+=': 'Ajouter « += » (ajouter à la variable)',
  '-=': 'Ajouter « -= » (retirer à la variable)',
  '*=': 'Ajouter « *= » (multiplier la variable)',
};

function OpContent({ op, code }: { op: string; code?: boolean }) {
  // ligne de code : le signe tel qu'il s'écrit en Python
  if (code) return <code className="sc-codeline">{op}</code>;
  if (op === '²')
    return (
      <>
        <span className="sc-ghost">a</span>²
      </>
    );
  if (op === '√')
    return (
      <>
        √<span className="sc-ghost">a</span>
      </>
    );
  if (op === '!')
    return (
      <>
        <span className="sc-ghost">n</span>!
      </>
    );
  if (op === '^')
    return (
      <>
        <span className="sc-ghost">a</span>^<span className="sc-ghost">n</span>
      </>
    );
  // matrices (moteur v30) : A⁻¹, Aᵀ
  if (op === '⁻¹' || op === 'ᵀ')
    return (
      <>
        <span className="sc-ghost">A</span>
        {op}
      </>
    );
  // un exposant (moteurs v30 et v31) : a³, x¹⁰
  if (SUP_OP_RE.test(op))
    return (
      <>
        <span className="sc-ghost">a</span>
        {op}
      </>
    );
  return <>{disp(op)}</>;
}

/** un exposant posé d'un bout : « ³ », « ¹⁰ » */
const SUP_OP_RE = /^[⁰¹²³⁴⁵⁶⁷⁸⁹]+$/;
const SUP_DIGITS = '⁰¹²³⁴⁵⁶⁷⁸⁹';
/** libellé d'un exposant : « au cube », « puissance 4 » */
export function supOpLabel(op: string): string | undefined {
  if (!SUP_OP_RE.test(op) || op === '²') return undefined;
  if (op === '³') return 'Ajouter « au cube »';
  const n = [...op].map((c) => SUP_DIGITS.indexOf(c)).join('');
  return `Ajouter « puissance ${n} »`;
}

interface Group {
  id: string;
  label: string;
  chips: (PaletteChip | AtomItem)[];
  answer?: boolean;
  learned?: boolean;
}

export function SheetPalette({
  state,
  session,
  onClose,
}: {
  state: SessionState;
  session: ScratchSession;
  /** « J'ai fini » : referme la feuille */
  onClose?: () => void;
}) {
  const p = session.palette;
  const tools = p.tools ?? [];
  const disabled = state.locked;
  const boutsRef = useRef<HTMLDivElement>(null);
  const [toolsOpen, setToolsOpen] = useState(false);

  // un terme repris d'une ligne validée apparaît avec un petit rebond, une fois
  const seen = useRef(new Set<string>());
  const freshKeys = new Set<string>();
  state.learned.forEach((c) => {
    const k = chipKey(c.atoms);
    if (!seen.current.has(k)) freshKeys.add(k);
  });
  useEffect(() => {
    state.learned.forEach((c) => seen.current.add(chipKey(c.atoms)));
  }, [state.learned]);

  // coup de pouce sur une grandeur de la carte : le groupe s'ouvre
  const hintOnTool = !!state.highlight && tools.some((c) => chipKey(c.atoms) === state.highlight);
  useEffect(() => {
    if (hintOnTool) setToolsOpen(true);
  }, [hintOnTool]);

  // Pas de défilement : les bouts rapetissent tant qu'ils ne tiennent pas.
  // On repart de la plus grande taille quand le contenu ou la largeur change.
  const [size, setSize] = useState<PaletteSize>('l');
  const contentKey = [
    p.statement.length,
    state.learned.length,
    p.answers.length,
    p.values.length,
    p.ops.length,
    toolsOpen ? tools.length : 0,
    session.free ? 1 : 0,
  ].join(':');
  const [width, setWidth] = useState(0);
  const fitKey = `${contentKey}|${width}`;
  const fitKeyRef = useRef(fitKey);
  useLayoutEffect(() => {
    if (fitKeyRef.current !== fitKey) {
      fitKeyRef.current = fitKey;
      if (size !== 'l') {
        setSize('l');
        return;
      }
    }
    const el = boutsRef.current;
    if (!el) return;
    const next = nextPaletteSize(size, el.scrollHeight, el.clientHeight);
    if (next !== size) setSize(next);
  }, [size, fitKey]);
  useEffect(() => {
    const el = boutsRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    // seulement la LARGEUR (rotation, panneau) : la hauteur suit la taille des bouts
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect.width ?? 0);
      setWidth((prev) => (prev === w ? prev : w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // coup de pouce : le bout qui clignote est ramené en vue
  useEffect(() => {
    if (!state.highlight) return;
    const k =
      typeof CSS !== 'undefined' && CSS.escape
        ? CSS.escape(state.highlight)
        : state.highlight.replace(/["\\]/g, '\\$&');
    const el = boutsRef.current?.querySelector<HTMLElement>(`[data-key="${k}"]`);
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [state.highlight, toolsOpen]);

  const groups: Group[] = [
    { id: 'statement', label: 'Énoncé', chips: p.statement },
    { id: 'learned', label: 'Tes résultats', chips: state.learned, learned: true },
    { id: 'answers', label: 'Réponses proposées', chips: p.answers, answer: true },
    { id: 'values', label: 'Valeurs', chips: p.values },
  ].filter((g) => g.chips.length > 0);

  const add = (chip: { atoms: string[]; text?: string }) => {
    vibrate(8);
    session.addChip(chip);
  };

  const chipButton = (c: PaletteChip | AtomItem, g?: Group) => {
    const key = chipKey(c.atoms);
    return (
      <button
        key={key}
        type="button"
        className="sc-chip"
        data-kind={c.kind}
        data-key={key}
        data-sc-keep="all"
        data-answer={g?.answer ? '' : undefined}
        data-learned={g?.learned ? '' : undefined}
        data-fresh={g?.learned && freshKeys.has(key) ? '' : undefined}
        data-tool={g ? undefined : ''}
        data-pulse={state.highlight === key ? '' : undefined}
        aria-label={`Ajouter ${c.atoms.length === 1 ? spokenAtom(c.atoms[0]!) : disp(c.text)}`}
        disabled={disabled}
        onClick={() => add(c)}
      >
        {chipContent(c)}
      </button>
    );
  };

  const target = session.copyTarget();
  const hasDiv = p.ops.includes('÷');
  // moteur v42 : une inéquation « factor » propose le tableau de signes
  const offersTable = !session.free && signTableOffered(session.model);
  // lot 6.21 : un tableau à remplir (statistiques, proportionnalité, double entrée)
  const gridSheet = session.model.kernel?.plugin === 'tables';
  const canErase = !disabled && state.line.length > 0;

  return (
    // trace Python (moteur v52) : un bout « [1, 3] » est une liste, pas une matrice
    <MathListsContext.Provider value={!session.free && !!programOf(session.model)}>
      <div className="flex min-h-0 flex-col" data-testid="scratch-palette">
        <div
          ref={boutsRef}
          data-size={size}
          className="sc-palette max-h-[12.5rem] min-h-0 overflow-y-auto overscroll-contain px-3 pb-[max(0.625rem,env(safe-area-inset-bottom))] pt-2 md:max-h-[15rem] lg:max-h-none lg:flex-1 lg:px-4 lg:pb-4"
          aria-label="Bouts et opérations"
          data-tuto="bouts"
          role="group"
        >
          <div className="sc-palette-flow flex flex-wrap gap-1.5">
            {groups.map((g, gi) => (
              <Fragment key={g.id}>
                {gi > 0 && <span className="sc-sep lg:hidden" aria-hidden />}
                <p className="sc-fam-label hidden basis-full lg:mt-2 lg:block [&:first-child]:lg:mt-0">
                  {g.label}
                </p>
                <span role="group" aria-label={g.label} className="contents">
                  {g.chips.map((c) => chipButton(c, g))}
                </span>
              </Fragment>
            ))}

            {session.free && (
              <span
                role="group"
                aria-label="Chiffres"
                className="contents"
                data-testid="scratch-digits"
              >
                <span className="sc-sep lg:hidden" aria-hidden />
                <p className="sc-fam-label hidden basis-full lg:mt-2 lg:block">Chiffres</p>
                {FREE_DIGITS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className="sc-chip"
                    data-kind="num"
                    data-key={`digit-${d}`}
                    data-sc-keep="all"
                    aria-label={d === ',' ? 'Ajouter une virgule' : `Ajouter le chiffre ${d}`}
                    disabled={disabled}
                    onClick={() => {
                      vibrate(8);
                      session.addDigit(d);
                    }}
                  >
                    {d}
                  </button>
                ))}
              </span>
            )}

            {tools.length > 0 && (
              <>
                <span className="sc-sep lg:hidden" aria-hidden />
                <button
                  type="button"
                  className="sc-chip sc-tools-toggle"
                  data-kind="op"
                  data-sc-keep="all"
                  data-open={toolsOpen ? '' : undefined}
                  aria-expanded={toolsOpen}
                  aria-label={
                    toolsOpen ? 'Masquer les outils de la carte' : 'Afficher les outils de la carte'
                  }
                  title="Outils de la carte"
                  onClick={() => setToolsOpen((o) => !o)}
                >
                  <Hammer className="h-[18px] w-[18px]" aria-hidden />
                </button>
                {toolsOpen && (
                  <span
                    role="group"
                    aria-label="Outils de la carte"
                    className="contents"
                    data-testid="scratch-tools"
                  >
                    {tools.map((c) => chipButton(c))}
                  </span>
                )}
              </>
            )}

            {(groups.length > 0 || session.free || tools.length > 0) && (
              <span className="sc-sep lg:hidden" aria-hidden />
            )}
            <p className="sc-fam-label hidden basis-full lg:mt-2 lg:block">Opérations</p>
            <span
              role="group"
              aria-label="Opérations"
              className="contents"
              data-testid="scratch-ops"
            >
              <button
                type="button"
                className="sc-chip"
                data-kind="op"
                data-copy=""
                data-key="copy"
                data-tuto="copy"
                data-sc-keep="all"
                disabled={!target}
                aria-label={
                  target
                    ? `Recopier « ${lineText([target])} » depuis la ligne du dessus`
                    : 'Recopier : touche un bout d’une ligne du dessus pour le choisir'
                }
                onClick={() => {
                  vibrate(8);
                  session.copyNext();
                }}
              >
                <CopyIcon />
              </button>
              {offersTable && (
                <button
                  type="button"
                  className="sc-chip"
                  data-kind="op"
                  data-key="sign-table"
                  data-sc-keep="all"
                  aria-label={
                    gridSheet
                      ? 'Remplir le tableau'
                      : session.model.kernel?.plugin === 'variations'
                        ? 'Dresser le tableau de variations'
                        : 'Dresser un tableau de signes'
                  }
                  title={
                    gridSheet
                      ? 'Tableau'
                      : session.model.kernel?.plugin === 'variations'
                        ? 'Tableau de variations'
                        : 'Tableau de signes'
                  }
                  disabled={disabled}
                  onClick={() => {
                    vibrate(8);
                    if (gridSheet) openGridTableEditor(session);
                    else openSignTableEditor(session);
                  }}
                >
                  <Table2 className="h-5 w-5" aria-hidden />
                </button>
              )}
              {p.ops.map((op, oi) => {
                const key = chipKey([op]);
                const btn = (
                  <button
                    key={op}
                    type="button"
                    className="sc-chip"
                    data-kind={
                      op === 'ou' || /^[a-z]{2,3}$/.test(op) || /[a-zé]{4}/.test(op) ? 'word' : 'op'
                    }
                    data-key={key}
                    data-sc-keep="all"
                    data-tuto={oi === 0 ? 'ops' : undefined}
                    data-pulse={state.highlight === key ? '' : undefined}
                    aria-label={
                      (session.code
                        ? CODE_OP_LABEL[op]
                        : ((session.matrix ? MATRIX_OP_LABEL[op] : undefined) ??
                          OP_LABEL[op] ??
                          supOpLabel(op))) ??
                      (/^\[\d+\]$/.test(op)
                        ? `Ajouter « modulo ${op.slice(1, -1)} »`
                        : `Ajouter ${op}`)
                    }
                    disabled={disabled}
                    onClick={() => add({ atoms: [op], text: session.code ? op : disp(op) })}
                  >
                    <OpContent op={op} code={session.code} />
                  </button>
                );
                if (op !== '÷' || !hasDiv) return btn;
                return (
                  <Fragment key={op}>
                    {btn}
                    <button
                      type="button"
                      className="sc-chip sc-ftpl"
                      data-kind="op"
                      data-key="frac"
                      data-sc-keep="all"
                      aria-label="Ajouter une fraction à deux cases"
                      disabled={disabled}
                      onClick={() => {
                        vibrate(8);
                        session.addFrac();
                      }}
                    >
                      <span className="sc-frac">
                        <span className="sc-fnum">
                          <i />
                        </span>
                        <span className="sc-fden">
                          <i />
                        </span>
                      </span>
                    </button>
                  </Fragment>
                );
              })}
              <button
                type="button"
                className="sc-chip"
                data-kind="op"
                data-key="erase"
                data-sc-keep="all"
                data-tuto="erase"
                aria-label="Effacer le dernier bout"
                disabled={!canErase}
                onClick={() => {
                  vibrate(8);
                  session.removeLast();
                }}
              >
                <Delete className="h-5 w-5" aria-hidden />
              </button>
            </span>
            {onClose && (
              <button
                type="button"
                className="sc-chip sc-done"
                data-sc-keep="all"
                data-tuto="close"
                aria-label="J’ai fini : fermer la feuille"
                title="J’ai fini"
                onClick={onClose}
              >
                <Check className="h-5 w-5" aria-hidden />
              </button>
            )}
          </div>
        </div>
        {offersTable &&
          (gridSheet ? <GridTableHost session={session} /> : <SignTableHost session={session} />)}
      </div>
    </MathListsContext.Provider>
  );
}
